import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatFileSize } from '../utils/formatFileSize';

export interface SupportingDocument {
  id: string;
  fileName: string;
  description?: string;
  uploadedByName: string;
  uploadedAt: string;
  fileSize: number;
  contentType?: string;
}

export interface SupportingDocumentsTableProps {
  documents: SupportingDocument[];
  onView: (documentId: string, fileName: string, contentType: string) => void;
  onDownload: (documentId: string, fileName: string) => void;
  onDelete: (documentId: string, fileName: string) => void;
  canEdit?: boolean;
  onEdit?: (documentId: string, fileName: string, description: string) => void;
  formatDate: (date: string) => string;
}

export function SupportingDocumentsTable({
  documents,
  onView,
  onDownload,
  onDelete,
  canEdit = false,
  onEdit,
  formatDate,
}: SupportingDocumentsTableProps) {
  if (documents.length === 0) {
    return null;
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>File Name</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Uploaded By</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Uploaded At</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Size</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }} align="right">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {documents.map((doc) => {
            const desc = doc.description?.trim();
            const truncated = desc && desc.length > 50 ? desc.slice(0, 50) + '…' : desc;
            return (
              <TableRow key={doc.id}>
                <TableCell>{doc.fileName}</TableCell>
                <TableCell>
                  {desc ? (
                    truncated !== desc ? (
                      <Tooltip title={desc}>
                        <span style={{ cursor: 'default' }}>{truncated}</span>
                      </Tooltip>
                    ) : (
                      desc
                    )
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>{doc.uploadedByName}</TableCell>
                <TableCell>{formatDate(doc.uploadedAt)}</TableCell>
                <TableCell>{formatFileSize(doc.fileSize)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Open in new tab">
                    <IconButton
                      size="small"
                      aria-label={`View ${doc.fileName}`}
                      onClick={() => onView(doc.id, doc.fileName, doc.contentType || '')}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    size="small"
                    aria-label={`Download ${doc.fileName}`}
                    onClick={() => onDownload(doc.id, doc.fileName)}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                  {canEdit && onEdit && (
                    <Tooltip title="Edit filename and description">
                      <IconButton
                        size="small"
                        aria-label={`Edit ${doc.fileName}`}
                        onClick={() => onEdit(doc.id, doc.fileName, doc.description || '')}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canEdit && (
                    <IconButton
                      size="small"
                      color="error"
                      aria-label={`Delete ${doc.fileName}`}
                      onClick={() => onDelete(doc.id, doc.fileName)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default SupportingDocumentsTable;
